import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { StyleSheet, View, useWindowDimensions } from 'react-native'
import { screens } from '@chillout/core'
import type { AdminAccount } from '../auth/adminAccount'
import type { AdminSectionRouteName } from '../adminRoutes'
import { AdminMenuContext } from './AdminMenuContext'
import { AdminMenuPanel } from './AdminMenuPanel'

interface AdminMenuProps {
  activeRouteName: AdminSectionRouteName
  adminAccount: AdminAccount
  children: ReactNode
  onRoutePress: (routeName: AdminSectionRouteName) => void
  onSignOut: () => void
}

export function AdminMenu({
  activeRouteName,
  adminAccount,
  children,
  onRoutePress,
  onSignOut,
}: AdminMenuProps) {
  const { width } = useWindowDimensions()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const isDesktop = width >= screens['2xl']
  const isTablet = width >= screens.lg && width < screens['2xl']
  const isMobile = !isDesktop && !isTablet
  const menuContext = useMemo(
    () => ({ onOpenMenu: isMobile ? () => setIsMenuOpen(true) : undefined, isMenuOpen: isMobile && isMenuOpen }),
    [isMobile, isMenuOpen],
  )

  function handleRoutePress(routeName: AdminSectionRouteName) {
    onRoutePress(routeName)
    setIsMenuOpen(false)
  }

  return (
    <View className="flex-1 flex-row bg-canvas-primary" style={styles.layoutRoot}>
      {isDesktop ? (
        <AdminMenuPanel
          activeRouteName={activeRouteName}
          adminAccount={adminAccount}
          variant="full"
          presentation="inline"
          onRoutePress={onRoutePress}
          onSignOut={onSignOut}
        />
      ) : null}

      {isTablet ? (
        <AdminMenuPanel
          activeRouteName={activeRouteName}
          adminAccount={adminAccount}
          variant="full"
          presentation="inlineExpandable"
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onOpen={() => setIsMenuOpen(true)}
          onRoutePress={handleRoutePress}
          onSignOut={onSignOut}
        />
      ) : null}

      <View className="flex-1">
        <AdminMenuContext.Provider value={menuContext}>{children}</AdminMenuContext.Provider>
      </View>

      {isMobile ? (
        <AdminMenuPanel
          activeRouteName={activeRouteName}
          adminAccount={adminAccount}
          variant="full"
          presentation="overlay"
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onRoutePress={handleRoutePress}
          onSignOut={onSignOut}
        />
      ) : null}
    </View>
  )
}

const styles = StyleSheet.create({
  layoutRoot: {
    position: 'relative',
  },
})
